<template>
  <div class="device-availability">
    <div v-if="scheduleCreated && !screentimeExhausted"
         class="device-availability-control device-availability-control-margin-bottom">
      <div class="label">
        {{ $t("deviceAvailability.runSchedule") }}
      </div>

      <base-checkbox :value="scheduleRunning"
                     @input="selectSchedule" />
    </div>

    <div v-if="!screentimeExhausted"
         class="device-availability-group">
      <div class="device-availability-control">
        <div class="label">
          {{ $t("deviceAvailability.enableInternet") }}
        </div>

        <base-checkbox :value="parentalStandardMode"
                       @input="selectParentalMode('Standard')" />
      </div>

      <div v-if="scheduleCreated"
           class="device-availability-title">
        {{ $t("deviceAvailability.ignoreSchedule") }}
      </div>

      <div class="device-availability-control device-availability-control-margin-top">
        <div class="label">
          {{ $t("deviceAvailability.disableInternet") }}
        </div>

        <base-checkbox :value="parentalNoInternetMode"
                       @input="selectParentalMode('No Internet')" />
      </div>

      <div v-if="scheduleCreated"
           class="device-availability-title">
        {{ $t("deviceAvailability.ignoreSchedule") }}
      </div>

      <div class="device-availability-control device-availability-control-margin-top">
        <div class="label">
          {{ $t("deviceAvailability.pauseDevice") }}
        </div>

        <base-checkbox :value="parentalPauseDeviceMode"
                       @input="selectParentalMode('Pause Device')" />
      </div>

      <div v-if="scheduleCreated"
           class="device-availability-title">
        {{ $t("deviceAvailability.ignoreSchedule") }}
      </div>
    </div>

    <base-button v-if="screentimeExhausted"
                 class="device-availability-btn device-availability-btn-adjust device-availability-btn-no-margin"
                 color="primary"
                 @click="adjustScreentime">
      <div class="device-availability-btn-text">
        {{ $t("adjustScreenTime") }}
      </div>
    </base-button>

    <base-button v-if="scheduleCreated && showScheduleBtn && screentimeExhausted"
                 class="device-availability-btn device-availability-btn-adjust device-availability-btn-no-margin"
                 color="primary"
                 @click="editSchedule">
      <div class="device-availability-btn-text">
        {{ $t("deviceAvailability.editSchedule") }}
      </div>
    </base-button>

    <div v-if="scheduleCreated && showScheduleBtn && !screentimeExhausted"
         class="device-availability-btn"
         @click="editSchedule">
         <div class="device-availability-btn-text">
           {{ $t("deviceAvailability.editSchedule") }}
          </div>

         <div class="dropdown-option-arrow" />
    </div>

    <div v-if="!scheduleCreated && !screentimeExhausted && showScheduleBtn"
         class="device-availability-btn"
         @click="editSchedule">
         <div class="device-availability-btn-text">
           {{ $t("deviceAvailability.createSchedule") }}
          </div>

         <div class="dropdown-option-arrow" />
    </div>

    <base-button class="device-availability-btn device-availability-btn-confirm"
                 color="secondary"
                 @click="confirm">
      <div class="device-availability-btn-confirm-btn-text">
        {{ $t('ok') }}
      </div>
    </base-button>
  </div>
</template>

<script>
  import childApi from '@/api/child.js'
  import errorNotification from '@/mixins/errorNotification.js'

  import * as intl from '@/services/intl'
  import analytics from '@/services/analytics.js'

  const duration = -1

  export default {
    mixins: [
      errorNotification
    ],

    props: {
      childId: {
        type: String,
        required: true
      },

      showScheduleBtn: {
        type: Boolean,
        default: true
      }
    },

    data() {
      return {
        selectedMode: null
      }
    },

    created() {
      this.selectedMode = this.child.modes.find(mode => mode.id === this.child.current_rule.mode_id)
      this.selectedMode.reason = this.child.current_rule.reason
    },

    computed: {
      scheduleRunning() {
        return this.selectedMode.reason === 'schedule'
      },

      scheduleCreated() {
        return this.child.time_blocks.length
      },

      child() {
        return this.getChildById(this.childId)
      },

      screentimeExhausted() {
        if (!this.child || !this.child.latest_accumulated_screentime) {
          return false
        }

        let usedScreenTime = Math.floor(this.child.latest_accumulated_screentime.sum_in_seconds / 60)

        return usedScreenTime >= this.screenTimeLimit
      },

      screenTimeLimit() {
        if (intl.isToday(this.child.settings.screen_time_override_date)) {
          return this.child.settings.screen_time_override_in_minutes
        }

        return this.child.settings.screen_time_limit_in_minutes[intl.getDayOfTheWeek()]
      },

      parentalStandardMode() {
        return !_.isNil(this.selectedMode) && this.selectedMode.name === 'Standard' && this.selectedMode.reason === 'parental_override'
      },

      parentalNoInternetMode() {
        return !_.isNil(this.selectedMode) && this.selectedMode.name === 'No Internet' && this.selectedMode.reason === 'parental_override'
      },

      parentalPauseDeviceMode() {
        return !_.isNil(this.selectedMode) && this.selectedMode.name === 'Pause Device' && this.selectedMode.reason === 'parental_override'
      },

      ...mapGetters('children', {
        getChildById: 'getById',
      }),

      ...mapGetters('auth', {
        currentUser: 'currentUser'
      })
    },

    methods: {
      editSchedule() {
        this.$router.push({
          name: 'children.single.schedule',
          params: {
            id: this.child.id
          }
        })

        this.$emit('close')
      },

      adjustScreentime() {
        this.showModal('screentime', {
          child: this.child
        })
      },

      selectSchedule() {
        this.selectedMode = {
          ...this.selectedMode,
          reason: 'schedule'
        }
      },

      selectParentalMode(mode_name) {
        this.selectedMode = {
          ...this.selectedMode,
          name: mode_name,
          reason: 'parental_override'
        }
      },

      removeParentalMode() {
        analytics.sendEvent('Pk_SetSchedule')

        childApi.removeChildParentalMode(this.child.id).then(() => {
          this.$store.dispatch('children/loadStatus', this.child.id)
        })
      },

      setNewMode() {
        analytics.sendEvent('Pk_SetRule')

        childApi.setChildParentalMode({
          user_id: this.child.id,
          mode_id: this.getModeId(this.selectedMode.name),
          duration_in_minutes: duration
        }).then(() => {
          this.$store.dispatch('children/loadStatus', this.child.id)
        }).catch(() => {
          this.failedRequestNotification()
        })
      },

      confirm() {
        if (this.screentimeExhausted) {
          this.$emit('close')

          return
        }

        if (this.scheduleRunning) {
          this.removeParentalMode()
          this.$emit('close')

          return
        }

        this.setNewMode()
        this.$emit('close')
      },

      getModeId(modeType) {
        let pickedMode = this.child.modes.find(mode => mode.name === modeType)

        if (!pickedMode) {
          return ''
        }

        return pickedMode.id
      },

      showFailNotification() {
        this.$notify({
          type: 'error',
          text: 'Failed to apply mode'
        })
      }
    }
  }

</script>

<style lang="scss" scoped>
  .circular-loader {
    width: 100%;
    height: 100%;
    min-height: 400px;

    display: flex;
    justify-content: center;
    align-items: center;
  }

  .device-availability {
    padding-top: 20px;

    &-title {
      font-style: normal;
      font-weight: normal;
      font-size: 16px;
      line-height: 24px;

      color: #8e8e8e;
    }

    &-control {
      display: flex;
      align-items: center;
      justify-content: space-between;

      font-style: normal;
      font-weight: 500;
      font-size: 22px;
      line-height: 26px;

      &-margin-top {
        margin-top: 44px;
      }

      &-margin-bottom {
        margin-bottom: 44px;
      }
    }

    &-btn {
      width: 100%;

      display: flex;
      align-items: center;
      justify-content: space-between;

      cursor: pointer;
      margin-top: 32px;
      padding-right: 6px;

      &-no-margin {
        margin: 0;
      }

      &-text {
        font-style: normal;
        font-size: 20px;
        line-height: 29px;
      }

      &-adjust {
        height: 75px;
        margin-bottom: 32px;
      }

      &-confirm {
        height: 75px;
        width: 100%;

        &-btn-text{
          font-style: normal;
          font-weight: bold;
          font-size: 24px;
          line-height: 29px;
          text-align: center;
        }
      }

      .dropdown-option-arrow {
        width: 13px;
        height: 13px;
        border-bottom: 1px solid #b9b9b9;
        border-right: 1px solid #b9b9b9;
        transform: rotate(-45deg);
      }
    }
  }
</style>